import { api, ApiError } from "../../api/client";
import {
  appointmentPosition,
  calendarHourHeight,
  dayKey,
  daysOfWeek,
  dropStartMinutes,
  snappedMinutes,
  startOfWeek,
} from "../../calendar_week";
import type { AppData } from "../../types";
import { button, el } from "../atoms/dom";
import { isoDay, timeText } from "./dashboard_page";

const hours = Array.from({ length: 24 }, (_, hour) => hour);

export function calendarPage(
  data: AppData,
  onOpenAppointment: (appointmentId: string) => void,
) {
  let selectedDate = new Date();
  let selectedStatus = "all";
  let firstRender = true;
  let draggingAppointmentId: string | null = null;
  let recentlyDraggedAppointmentId: string | null = null;
  let reschedulingAppointmentId: string | null = null;
  let noticeTimer: number | null = null;
  const branchId = data._meta.selectedContext.branchId;
  const organizationId = data._meta.selectedContext.organizationId;
  const canWrite = (data.permissionsByOrganization[organizationId] ?? [])
    .includes("appointments.write");
  const calendarHost = el("div", { className: "week-calendar" });
  const datePicker = el("input", {
    className: "native-date",
    attrs: { type: "date", "aria-label": "Takvimden hafta seç" },
  });
  const rangeButton = el("button", {
    className: "week-range-button",
    attrs: { type: "button", "aria-label": "Hafta seç" },
  });
  const statusFilters = buildStatusFilters();
  const calendarNotice = el("div", {
    className: "calendar-notice",
    attrs: { role: "status", "aria-live": "polite", hidden: "" },
  });

  rangeButton.addEventListener("click", () => {
    datePicker.value = dayKey(selectedDate);
    datePicker.showPicker?.();
    datePicker.click();
  });
  datePicker.addEventListener("change", () => {
    if (!datePicker.value) return;
    selectedDate = new Date(`${datePicker.value}T12:00:00`);
    renderCalendar();
  });

  const createButton = button("+ Yeni randevu", () => {
    openCreateSheet(defaultAppointmentDate());
  });
  createButton.classList.add("calendar-create-button");
  if (!canWrite) {
    createButton.disabled = true;
    createButton.title = "Randevu oluşturma yetkiniz bulunmuyor";
  }

  const page = el(
    "div",
    { className: "calendar-week-page" },
    el(
      "section",
      { className: "week-toolbar" },
      el("div", { className: "week-toolbar__create" }, createButton),
      el(
        "div",
        { className: "week-toolbar__navigation" },
        button("Bugün", () => {
          selectedDate = new Date();
          renderCalendar();
        }, "quiet"),
        el("button", {
          className: "day-arrow day-arrow--compact",
          text: "‹",
          attrs: { type: "button", "aria-label": "Önceki hafta" },
          onClick: () => moveWeek(-1),
        }),
        el("button", {
          className: "day-arrow day-arrow--compact",
          text: "›",
          attrs: { type: "button", "aria-label": "Sonraki hafta" },
          onClick: () => moveWeek(1),
        }),
        rangeButton,
        datePicker,
      ),
      statusFilters,
    ),
    canWrite
      ? el("p", {
        className: "week-drag-hint",
        text: "Randevuyu sürükleyerek istediğiniz gün ve saate taşıyabilirsiniz.",
      })
      : null,
    calendarHost,
    calendarNotice,
  );

  function moveWeek(direction: number) {
    selectedDate.setDate(selectedDate.getDate() + direction * 7);
    renderCalendar();
  }

  function buildStatusFilters() {
    const root = el("div", { className: "filter-tabs week-status-filters" });
    for (const [value, label] of [
      ["all", "Tümü"],
      ["confirmed", "Onaylandı"],
      ["pending", "Bekliyor"],
      ["cancelled", "İptal"],
    ]) {
      root.append(el("button", {
        className: `filter-tab ${selectedStatus === value ? "is-active" : ""}`,
        text: label,
        attrs: { type: "button", "data-status": value },
        onClick: () => {
          selectedStatus = value;
          root.querySelectorAll<HTMLButtonElement>("button").forEach((node) => {
            node.classList.toggle(
              "is-active",
              node.dataset.status === value,
            );
          });
          renderCalendar();
        },
      }));
    }
    return root;
  }

  function renderCalendar() {
    rangeButton.textContent = weekRange(selectedDate);
    const previousScroller = calendarHost.querySelector<HTMLElement>(
      ".week-calendar__scroll",
    );
    const previousScrollTop = previousScroller?.scrollTop;
    const previousScrollLeft = previousScroller?.scrollLeft ?? 0;
    const week = daysOfWeek(selectedDate);
    const surface = el("div", { className: "week-calendar__surface" });
    const header = el(
      "div",
      { className: "week-calendar__header" },
      el("div", {
        className: "week-calendar__timezone",
        text: timezoneLabel(data._meta.timezone),
      }),
    );
    for (const day of week) header.append(buildDayHeader(day));

    const body = el(
      "div",
      {
        className: "week-calendar__body",
        attrs: { style: `height:${24 * calendarHourHeight}px` },
      },
      buildTimeGutter(),
    );
    for (const day of week) body.append(buildDayColumn(day));
    surface.append(header, body);
    const scroller = el(
      "div",
      { className: "week-calendar__scroll" },
      surface,
    );
    calendarHost.replaceChildren(scroller);
    requestAnimationFrame(() => {
      scroller.scrollTop = previousScrollTop ??
        (firstRender ? 7.5 * calendarHourHeight : 0);
      scroller.scrollLeft = previousScrollLeft;
      firstRender = false;
    });
  }

  function buildDayHeader(day: Date) {
    const today = isoDay(day) === isoDay(new Date());
    return el(
      "div",
      { className: `week-day-header ${today ? "is-today" : ""}` },
      el("span", { text: shortWeekday(day) }),
      el("strong", { text: String(day.getDate()) }),
    );
  }

  function buildTimeGutter() {
    const gutter = el("div", { className: "week-time-gutter" });
    for (const hour of hours) {
      gutter.append(el("time", {
        text: `${String(hour).padStart(2, "0")}:00`,
        attrs: { style: `top:${hour * calendarHourHeight}px` },
      }));
    }
    return gutter;
  }

  function buildDayColumn(day: Date) {
    const today = isoDay(day) === isoDay(new Date());
    const column = el("div", {
      className: `week-day-column ${today ? "is-today" : ""}`,
      attrs: {
        role: "gridcell",
        "aria-label": `${fullDay(day)} için boş saate randevu ekle`,
      },
      onClick: (event) => {
        if (!canWrite || event.target !== column) return;
        const rect = column.getBoundingClientRect();
        const minutes = snappedMinutes(event.clientY - rect.top);
        const date = new Date(day);
        date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
        openCreateSheet(date);
      },
    });
    column.addEventListener("dragover", (event) => {
      const appointment = draggedAppointment(event);
      if (!appointment || !canWrite || reschedulingAppointmentId !== null) {
        return;
      }
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
      calendarHost.querySelectorAll(".week-day-column.is-drop-target")
        .forEach((item) => item.classList.remove("is-drop-target"));
      column.classList.add("is-drop-target");
      showDropPreview(column, appointment, event.clientY);
      autoScrollCalendar(event.clientY);
    });
    column.addEventListener("dragleave", (event) => {
      if (
        event.relatedTarget instanceof Node &&
        column.contains(event.relatedTarget)
      ) return;
      column.classList.remove("is-drop-target");
      column.querySelector(".week-drop-preview")?.remove();
    });
    column.addEventListener("drop", (event) => {
      const appointment = draggedAppointment(event);
      event.preventDefault();
      event.stopPropagation();
      if (!appointment || !canWrite || reschedulingAppointmentId !== null) {
        clearDragPresentation();
        return;
      }
      const durationMinutes = appointmentDurationMinutes(appointment);
      const rect = column.getBoundingClientRect();
      const minutes = dropStartMinutes(
        event.clientY - rect.top,
        durationMinutes,
      );
      clearDragPresentation();
      void rescheduleAppointment(appointment, day, minutes);
    });
    const dayAppointments = data.appointments
      .filter((appointment) => appointment.branchId === branchId)
      .filter((appointment) =>
        isoDay(new Date(appointment.startsAt)) === isoDay(day)
      )
      .filter((appointment) =>
        selectedStatus === "all" || appointment.status === selectedStatus
      )
      .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
    for (const appointment of dayAppointments) {
      const customer = data.customers.find((item) =>
        item.id === appointment.customerId
      );
      const service = data.services.find((item) =>
        item.id === appointment.serviceId
      );
      const position = appointmentPosition(
        new Date(appointment.startsAt),
        new Date(appointment.endsAt),
      );
      const canMove = canWrite &&
        appointment.status !== "cancelled" &&
        reschedulingAppointmentId === null;
      const card = el(
        "button",
        {
          className: `week-appointment week-appointment--${appointment.status} ${reschedulingAppointmentId === appointment.id ? "is-saving" : ""}`,
          attrs: {
            type: "button",
            style: `top:${position.top}px;height:${position.height}px`,
            "data-appointment-id": appointment.id,
            draggable: canMove ? "true" : "false",
            title: canMove
              ? "Başka gün veya saate taşımak için sürükleyin"
              : "Randevu detayını aç",
            "aria-label": `${customer?.displayName ?? "Anonim hasta"}, ${service?.name ?? "Hizmet"} randevusunu aç`,
          },
          onClick: (event) => {
            event.stopPropagation();
            if (recentlyDraggedAppointmentId === appointment.id) return;
            onOpenAppointment(appointment.id);
          },
        },
        el("time", { text: timeText(appointment.startsAt) }),
        el("strong", { text: customer?.displayName ?? "Anonim hasta" }),
        el("span", { text: service?.name ?? "Hizmet" }),
      );
      card.addEventListener("dragstart", (event) => {
        if (!canMove || !event.dataTransfer) {
          event.preventDefault();
          return;
        }
        draggingAppointmentId = appointment.id;
        recentlyDraggedAppointmentId = appointment.id;
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", appointment.id);
        requestAnimationFrame(() => card.classList.add("is-dragging"));
      });
      card.addEventListener("dragend", () => {
        clearDragPresentation();
      });
      column.append(card);
    }
    if (today) {
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      column.append(el("span", {
        className: "week-current-time",
        attrs: {
          style: `top:${minutes / 60 * calendarHourHeight}px`,
        },
      }));
    }
    return column;
  }

  function draggedAppointment(event: DragEvent) {
    const transferredId = event.dataTransfer?.getData("text/plain");
    const appointmentId = transferredId || draggingAppointmentId;
    if (!appointmentId) return null;
    return data.appointments.find((item) => item.id === appointmentId) ?? null;
  }

  function appointmentDurationMinutes(
    appointment: (typeof data.appointments)[number],
  ) {
    return Math.max(
      15,
      Math.round(
        (new Date(appointment.endsAt).getTime() -
          new Date(appointment.startsAt).getTime()) / 60_000,
      ),
    );
  }

  function showDropPreview(
    column: HTMLElement,
    appointment: (typeof data.appointments)[number],
    clientY: number,
  ) {
    const durationMinutes = appointmentDurationMinutes(appointment);
    const rect = column.getBoundingClientRect();
    const minutes = dropStartMinutes(clientY - rect.top, durationMinutes);
    calendarHost.querySelectorAll(".week-drop-preview").forEach((item) => {
      item.remove();
    });
    column.append(el("div", {
      className: "week-drop-preview",
      text: `${minuteLabel(minutes)} – ${minuteLabel(minutes + durationMinutes)}`,
      attrs: {
        style: `top:${minutes / 60 * calendarHourHeight}px;height:${Math.max(30, durationMinutes / 60 * calendarHourHeight)}px`,
      },
    }));
  }

  function autoScrollCalendar(clientY: number) {
    const scroller = calendarHost.querySelector<HTMLElement>(
      ".week-calendar__scroll",
    );
    if (!scroller) return;
    const rect = scroller.getBoundingClientRect();
    const threshold = 64;
    if (clientY < rect.top + threshold) scroller.scrollBy({ top: -18 });
    if (clientY > rect.bottom - threshold) scroller.scrollBy({ top: 18 });
  }

  function clearDragPresentation() {
    draggingAppointmentId = null;
    const completedDragId = recentlyDraggedAppointmentId;
    if (completedDragId) {
      window.setTimeout(() => {
        if (recentlyDraggedAppointmentId === completedDragId) {
          recentlyDraggedAppointmentId = null;
        }
      }, 250);
    }
    calendarHost.querySelectorAll(
      ".week-appointment.is-dragging, .week-day-column.is-drop-target",
    ).forEach((item) => {
      item.classList.remove("is-dragging", "is-drop-target");
    });
    calendarHost.querySelectorAll(".week-drop-preview").forEach((item) => {
      item.remove();
    });
  }

  async function rescheduleAppointment(
    appointment: (typeof data.appointments)[number],
    targetDay: Date,
    minutes: number,
  ) {
    const oldStartsAt = appointment.startsAt;
    const oldEndsAt = appointment.endsAt;
    const durationMinutes = appointmentDurationMinutes(appointment);
    const nextStart = new Date(targetDay);
    nextStart.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    if (nextStart.getTime() === new Date(oldStartsAt).getTime()) return;
    const nextEnd = new Date(nextStart.getTime() + durationMinutes * 60_000);

    appointment.startsAt = nextStart.toISOString();
    appointment.endsAt = nextEnd.toISOString();
    reschedulingAppointmentId = appointment.id;
    renderCalendar();
    showCalendarNotice("Randevu taşınıyor…", "loading");
    try {
      await api.updateAppointment(appointment.id, {
        serviceId: appointment.serviceId,
        staffId: appointment.staffId,
        startsAt: appointment.startsAt,
      });
      reschedulingAppointmentId = null;
      renderCalendar();
      showCalendarNotice(
        `Randevu ${fullDateTime(nextStart)} tarihine taşındı.`,
        "success",
      );
    } catch (error) {
      appointment.startsAt = oldStartsAt;
      appointment.endsAt = oldEndsAt;
      reschedulingAppointmentId = null;
      renderCalendar();
      showCalendarNotice(rescheduleErrorMessage(error), "error");
    }
  }

  function showCalendarNotice(
    message: string,
    kind: "loading" | "success" | "error",
  ) {
    if (noticeTimer !== null) window.clearTimeout(noticeTimer);
    calendarNotice.textContent = message;
    calendarNotice.className = `calendar-notice calendar-notice--${kind}`;
    calendarNotice.hidden = false;
    if (kind !== "loading") {
      noticeTimer = window.setTimeout(() => {
        calendarNotice.hidden = true;
        noticeTimer = null;
      }, 4500);
    }
  }

  function defaultAppointmentDate() {
    const now = new Date();
    const visibleWeek = startOfWeek(selectedDate);
    const currentWeek = startOfWeek(now);
    const value = dayKey(visibleWeek) === dayKey(currentWeek)
      ? now
      : new Date(visibleWeek);
    const minutes = value.getHours() * 60 + value.getMinutes();
    const rounded = Math.min(23 * 60 + 30, Math.ceil(minutes / 30) * 30);
    value.setHours(Math.floor(rounded / 60), rounded % 60, 0, 0);
    if (value.getHours() < 8) value.setHours(9, 0, 0, 0);
    if (value.getHours() >= 18) {
      value.setDate(value.getDate() + 1);
      value.setHours(9, 0, 0, 0);
    }
    return value;
  }

  function openCreateSheet(initialDate: Date) {
    const customers = [...data.customers].sort((left, right) =>
      left.displayName.localeCompare(right.displayName, "tr")
    );
    const services = data.services
      .filter((service) => service.isBookable)
      .filter((service) =>
        service.branchIds.length === 0 || service.branchIds.includes(branchId)
      );
    const staff = data.staff.filter((member) =>
      member.active && member.branchId === branchId
    );
    const customerSelect = selectField(
      "Müşteri / hasta seçin",
      customers.map((customer) => ({
        value: customer.id,
        label: `${customer.displayName} · ${customer.phoneNumber}`,
      })),
    );
    const serviceSelect = selectField(
      "Hizmet seçin",
      services.map((service) => ({
        value: service.id,
        label: `${service.name} · ${service.durationMinutes} dakika`,
      })),
    );
    const staffSelect = selectField(
      "Atanmamış",
      staff.map((member) => ({
        value: member.id,
        label: `${member.name}${member.profession ? ` · ${member.profession}` : ""}`,
      })),
    );
    const dateInput = el("input", {
      className: "form-control",
      attrs: {
        type: "date",
        required: "",
        value: dayKey(initialDate),
      },
    });
    const timeInput = el("input", {
      className: "form-control",
      attrs: {
        type: "time",
        required: "",
        step: "900",
        value: `${String(initialDate.getHours()).padStart(2, "0")}:${String(initialDate.getMinutes()).padStart(2, "0")}`,
      },
    });
    const durationHint = el("p", {
      className: "form-hint appointment-duration-hint",
    });
    const feedback = el("p", {
      className: "form-feedback",
      attrs: { role: "status", "aria-live": "polite" },
    });
    const submit = el("button", {
      className: "button button--primary",
      text: "Randevuyu oluştur",
      attrs: { type: "submit" },
    });
    const form = el(
      "form",
      { className: "appointment-create-form" },
      field("Müşteri / hasta", customerSelect),
      field("Yapılacak işlem", serviceSelect),
      field("Ekip üyesi", staffSelect),
      field("Tarih", dateInput),
      field("Saat", timeInput),
      durationHint,
      feedback,
      el(
        "div",
        { className: "modal-sheet__actions appointment-create-actions" },
        button("Vazgeç", close, "quiet"),
        submit,
      ),
    );
    const backdrop = el(
      "div",
      { className: "modal-backdrop", attrs: { role: "presentation" } },
      el(
        "section",
        {
          className: "modal-sheet appointment-create-sheet",
          attrs: {
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "create-appointment-title",
          },
        },
        el("div", { className: "modal-sheet__handle" }),
        el(
          "header",
          { className: "appointment-create-sheet__header" },
          el(
            "div",
            {},
            el("span", { className: "eyebrow", text: "YENİ RANDEVU" }),
            el("h2", {
              text: "Randevu oluştur",
              attrs: { id: "create-appointment-title" },
            }),
            el("p", {
              text: "Hasta, işlem ve saati seçerek takvime ekleyin.",
            }),
          ),
          el("button", {
            className: "sheet-close",
            text: "×",
            attrs: { type: "button", "aria-label": "Kapat" },
            onClick: close,
          }),
        ),
        form,
      ),
    );

    function updateDuration() {
      const service = services.find((item) =>
        item.id === serviceSelect.value
      );
      durationHint.textContent = service
        ? `Takvimde ${service.durationMinutes} dakikalık alan ayrılacak.`
        : "İşlem süresi seçtiğiniz hizmete göre otomatik hesaplanır.";
    }

    function close() {
      document.removeEventListener("keydown", onKeyDown);
      backdrop.remove();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }

    function showFeedback(message: string, isError = false) {
      feedback.textContent = message;
      feedback.classList.toggle("is-error", isError);
    }

    serviceSelect.addEventListener("change", updateDuration);
    updateDuration();
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) close();
    });
    document.addEventListener("keydown", onKeyDown);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (
        !customerSelect.value ||
        !serviceSelect.value ||
        !dateInput.value ||
        !timeInput.value
      ) {
        showFeedback(
          "Müşteri, işlem, tarih ve saat alanlarını doldurun.",
          true,
        );
        return;
      }
      submit.disabled = true;
      submit.textContent = "Oluşturuluyor…";
      try {
        const result = await api.createAppointment({
          branchId,
          customerId: customerSelect.value,
          serviceId: serviceSelect.value,
          staffId: staffSelect.value || null,
          startsAt: new Date(
            `${dateInput.value}T${timeInput.value}:00`,
          ).toISOString(),
        });
        const refreshed = await api.loadAppData();
        data.appointments = refreshed.appointments;
        data.notifications = refreshed.notifications;
        data.dashboardByBranch = refreshed.dashboardByBranch;
        selectedDate = new Date(`${dateInput.value}T12:00:00`);
        close();
        renderCalendar();
        requestAnimationFrame(() => {
          calendarHost.querySelector<HTMLElement>(
            `[data-appointment-id="${CSS.escape(result.id)}"]`,
          )?.scrollIntoView({ block: "center", behavior: "smooth" });
        });
      } catch (error) {
        showFeedback(createErrorMessage(error), true);
      } finally {
        submit.disabled = false;
        submit.textContent = "Randevuyu oluştur";
      }
    });
    if (customers.length === 0 || services.length === 0) {
      submit.disabled = true;
      showFeedback(
        customers.length === 0
          ? "Randevu oluşturmak için önce bir müşteri veya hasta kaydı bulunmalı."
          : "Randevu oluşturmak için aktif bir hizmet bulunmalı.",
        true,
      );
    }
    page.append(backdrop);
    requestAnimationFrame(() => customerSelect.focus());
  }

  renderCalendar();
  return page;
}

function field(label: string, control: HTMLElement) {
  return el(
    "label",
    { className: "form-field" },
    el("span", { text: label }),
    control,
  );
}

function selectField(
  placeholder: string,
  options: Array<{ value: string; label: string }>,
) {
  const select = el("select", { className: "form-control" });
  select.append(el("option", {
    text: placeholder,
    attrs: { value: "" },
  }));
  for (const option of options) {
    select.append(el("option", {
      text: option.label,
      attrs: { value: option.value },
    }));
  }
  return select;
}

function weekRange(value: Date) {
  const days = daysOfWeek(value);
  const first = days[0]!;
  const last = days[6]!;
  if (first.getMonth() === last.getMonth()) {
    return `${first.getDate()} – ${last.getDate()} ${monthYear(last)}`;
  }
  return `${first.getDate()} ${shortMonth(first)} – ${last.getDate()} ${monthYear(last)}`;
}

function monthYear(value: Date) {
  return new Intl.DateTimeFormat("tr-TR", {
    month: "long",
    year: "numeric",
  }).format(value);
}

function shortMonth(value: Date) {
  return new Intl.DateTimeFormat("tr-TR", { month: "short" }).format(value);
}

function shortWeekday(value: Date) {
  return new Intl.DateTimeFormat("tr-TR", { weekday: "short" })
    .format(value)
    .replace(".", "")
    .toLocaleUpperCase("tr");
}

function fullDay(value: Date) {
  return new Intl.DateTimeFormat("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(value);
}

function fullDateTime(value: Date) {
  return new Intl.DateTimeFormat("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(value);
}

function minuteLabel(minutes: number) {
  const safeMinutes = Math.min(24 * 60, Math.max(0, minutes));
  return `${String(Math.floor(safeMinutes / 60)).padStart(2, "0")}:${String(safeMinutes % 60).padStart(2, "0")}`;
}

function timezoneLabel(value: string) {
  if (value === "Europe/Istanbul") return "GMT+03";
  return value.replaceAll("_", " ");
}

function createErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.code === "appointment_conflict") {
    return "Seçtiğiniz işlem süresini karşılayacak boş zaman bulunmuyor. Başka bir saat seçin.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return "Randevu oluşturma yetkiniz bulunmuyor.";
  }
  if (error instanceof ApiError && error.code === "invalid_reference") {
    return "Müşteri, hizmet veya ekip üyesi artık kullanılamıyor. Sayfayı yenileyin.";
  }
  return "Randevu oluşturulamadı. Lütfen tekrar deneyin.";
}

function rescheduleErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.code === "appointment_conflict") {
    return "Bu saat aralığı dolu. Randevu eski gün ve saatine geri alındı.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return "Randevu taşıma yetkiniz bulunmuyor.";
  }
  if (error instanceof ApiError && error.code === "invalid_reference") {
    return "Randevuya bağlı hizmet veya ekip üyesi artık kullanılamıyor.";
  }
  return "Randevu taşınamadı ve eski gün ile saatine geri alındı.";
}
